import { OrderByDirection, ProgrammingStructureOrderByFields } from "@models/ISchema";

/**
 * Codes stored in the backend `programming_structure_sources` lookup table and
 * returned by `ProgrammingStructure.source`. The same value is accepted by
 * `ProgrammingStructureFilterFieldsInput.source`.
 */
export const STRUCTURE_SOURCE = {
  base: "BASE",
  user: "user-defined",
} as const;

export type StructureSourceCode = (typeof STRUCTURE_SOURCE)[keyof typeof STRUCTURE_SOURCE];

/**
 * Extra codes found in real scenario data that mean the same as a canonical
 * one. `programming_structure_sources` is a lookup table, so rows created
 * outside `ProgrammingStructure::getOrCreateSourceUsuario()` — integrations,
 * legacy data — can spell the same concept differently.
 *
 * Aliases are matched case-insensitively and are **inbound only**: they resolve
 * a `source` value coming back from the API into the right label, and a deep
 * link into the right checkbox. Queries always send the canonical code alone —
 * matching its own alias rows is the backend's responsibility.
 */
export const STRUCTURE_SOURCE_ALIASES: Record<StructureSourceCode, readonly string[]> = {
  [STRUCTURE_SOURCE.base]: [],
  [STRUCTURE_SOURCE.user]: ["Usuario"],
};

export interface SelectedSubject {
  code: string;
  name: string;
}

/**
 * A pair of mutually exclusive checkboxes: at most one is ever `true`. Both
 * unchecked means "unconstrained", and `buildFilterFields` also treats a
 * both-checked pair as unconstrained defensively — `BooleanFilterInput` only
 * exposes `is`, so "either value" can only be expressed by omitting the field.
 */
export interface BooleanFilterPair {
  yes: boolean;
  no: boolean;
}

export interface ProgrammingStructuresFilters {
  subject: SelectedSubject | null;
  structureCode: string;
  sources: Record<StructureSourceCode, boolean>;
  isPublished: BooleanFilterPair;
  isVisible: BooleanFilterPair;
}

export interface ProgrammingStructuresSort {
  field: ProgrammingStructureOrderByFields | null;
  direction: OrderByDirection | null;
}

/**
 * The primary action button label. `update` takes precedence: once a non-empty
 * filter set has been applied, any pending edit leaves the table stale.
 */
export type PrimaryActionLabel = "search" | "apply" | "update";

export interface ProgrammingStructuresSearchParams {
  filters: ProgrammingStructuresFilters;
  sort: ProgrammingStructuresSort;
  page: number;
}

export const EMPTY_FILTERS: ProgrammingStructuresFilters = {
  subject: null,
  structureCode: "",
  sources: {
    [STRUCTURE_SOURCE.base]: false,
    [STRUCTURE_SOURCE.user]: false,
  },
  isPublished: { yes: false, no: false },
  isVisible: { yes: false, no: false },
};

export const DEFAULT_SORT: ProgrammingStructuresSort = {
  field: ProgrammingStructureOrderByFields.CreatedAt,
  direction: OrderByDirection.Desc,
};
